import { NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  ViewEncapsulation,
  computed,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { IconSizeDirective } from 'src/app/shared/directives/icon-size.directive';
import { LoaderComponent } from 'src/app/shared/loading/loader/loader.component';
import { sidenavToggeling } from './animations';
import { SideNavItem } from './side-nav-item.model';

@Component({
  selector: 'app-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrl: './side-nav.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [sidenavToggeling],
  host: {
    class: 'side-nav-container',
    '[class.side-nav-container--expanded]': 'isExpanded()',
    '[class.side-nav-container--hover-opened]': 'hoverOpened()',
  },
  imports: [IconSizeDirective, MatIconModule, MatTooltipModule, NgStyle, RouterModule, LoaderComponent],
})
export class SideNavComponent implements OnDestroy {
  readonly sideNavItems = input.required<Array<SideNavItem>>();
  readonly isLoading = input.required<boolean>();
  readonly hoverExpandDelayMs = 200;

  readonly isExpanded = model<boolean>(true);

  readonly pinnedToggled = output<boolean>();

  readonly toggleState = signal<'in' | 'out'>('out');
  readonly #hasActiveChildren = signal(false);
  readonly hoverOpened = signal(false);
  readonly pinnedOpened = model<boolean>(false);
  #hoverExpandTimeout: ReturnType<typeof setTimeout> | null = null;

  readonly toggleIcon = computed(() => {
    const state = this.toggleState();
    const isExpanded = this.isExpanded();

    return state === 'in'
      ? isExpanded
        ? 'side-panel--close'
        : 'side-panel--open--filled'
      : isExpanded
        ? 'side-panel--close--filled'
        : 'side-panel--open';
  });

  onItemClick(item: SideNavItem): void {
    item.isExpanded = !item.isExpanded;
    this.#setNavItemState(item);
    if (item?.onClick) {
      item.onClick(item);
    }

    if (!this.isExpanded() && !!item?.children) {
      this.isExpanded.set(true);
      item.isExpanded = true;
    }
  }

  toggleOverlay(shouldOpen: boolean): void {
    if (this.pinnedOpened()) {
      // if the menu is pinned open, we don't want to toggle the hover state on mouse enter/leave
      return;
    }
    this.#clearHoverExpandTimeout();

    if (shouldOpen) {
      this.#hoverExpandTimeout = setTimeout(() => {
        if (this.pinnedOpened()) {
          return;
        }
        this.#setHoverState(true);
      }, this.hoverExpandDelayMs);
      return;
    }

    this.#setHoverState(false);
  }

  toggleSideNavPinned(): void {
    this.#clearHoverExpandTimeout();
    this.isExpanded.set(!this.isExpanded());
    this.pinnedOpened.set(this.isExpanded());
    this.pinnedToggled.emit(this.isExpanded());
  }

  ngOnDestroy(): void {
    this.#clearHoverExpandTimeout();
  }

  #setHoverState(isExpanded: boolean): void {
    this.isExpanded.set(isExpanded);
    this.toggleState.set(isExpanded ? 'in' : 'out');
    this.hoverOpened.set(isExpanded);
  }

  #clearHoverExpandTimeout(): void {
    if (this.#hoverExpandTimeout === null) {
      return;
    }

    clearTimeout(this.#hoverExpandTimeout);
    this.#hoverExpandTimeout = null;
  }

  #setNavItemState(selectedItem: SideNavItem) {
    this.#hasActiveChildren.set(this.getActiveChildrenState(selectedItem));
    this.sideNavItems().forEach(item => {
      if (selectedItem?.children) {
        selectedItem.isActive = selectedItem.isExpanded ? false : this.#hasActiveChildren();
      } else {
        selectedItem.isActive = item.id === selectedItem.id ? !item.isActive : false;
      }

      item?.children?.forEach(child => {
        if (!selectedItem?.children) {
          if (child.id === selectedItem.id) {
            selectedItem.isActive = !child.isActive;
          } else {
            child.isActive = false;
          }
        }

        child?.children?.forEach(grandChild => {
          if (!selectedItem?.children) {
            if (grandChild.id === selectedItem.id) {
              selectedItem.isActive = !grandChild.isActive;
            } else {
              grandChild.isActive = false;
            }
          }
        });
      });
    });
  }

  getActiveChildrenState(selectedItem: SideNavItem) {
    return !!selectedItem?.children?.some(e => e.isActive || e?.children?.some(g => g.isActive));
  }
}
